import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import GridItem from 'components/Grid/GridItem.js';
import EVChargingService from '../../services/EVChargingService';
import useAPI from 'useAPI';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function dollars(cents) {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatDateParts(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

function periodLabel({ year, monthIndex }) {
  return `${MONTH_NAMES[monthIndex]} ${year}`;
}

function periodToParam({ year, monthIndex }) {
  const mm = String(monthIndex + 1).padStart(2, '0');
  return `${year}-${mm}`;
}

function currentUtcPeriod() {
  const now = new Date();
  return { year: now.getUTCFullYear(), monthIndex: now.getUTCMonth() };
}

function isCurrentMonth(period) {
  const cur = currentUtcPeriod();
  return period.year === cur.year && period.monthIndex === cur.monthIndex;
}

function shiftPeriod(period, delta) {
  const totalMonths = period.year * 12 + period.monthIndex + delta;
  return {
    year: Math.floor(totalMonths / 12),
    monthIndex: ((totalMonths % 12) + 12) % 12,
  };
}

function entryLabel(entry) {
  switch (entry.type) {
    case 'top_up': return 'Top-up';
    case 'session_debit':
      return entry.billing_tier_name
        ? `Charging session — ${entry.billing_tier_name}`
        : 'Charging session';
    case 'refund': return 'Refund';
    case 'admin_credit': return entry.note ? `Credit — ${entry.note}` : 'Credit';
    default: return entry.type;
  }
}

export default function EVChargingStatements() {
  const api = useAPI();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(currentUtcPeriod());
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (p) => {
    try {
      setLoading(true);
      setError(null);
      const data = await EVChargingService.getStatement(periodToParam(p));
      setStatement(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Unable to load statement.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (api) {
      EVChargingService.init(api);
      load(period);
    }
  }, [api, period, load]);

  const atCurrentMonth = useMemo(() => isCurrentMonth(period), [period]);

  return (
    <AuthenticatedContainer>
      <GridItem xs={12} sm={12} md={8} align='center'>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: 360,
            mx: 'auto',
            mt: 1,
          }}
        >
          <IconButton
            aria-label='Back to wallet'
            onClick={() => navigate('/ev-charging/wallet')}
            sx={{ position: 'absolute', left: 0 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h5' style={{ color: '#324152' }}>
            EV CHARGING STATEMENT
          </Typography>
        </Box>
      </GridItem>

      {/* Period selector */}
      <GridItem xs={12} sm={12} md={8} align='center'>
        <Box
          sx={{
            maxWidth: 360,
            mx: 'auto',
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <IconButton
            aria-label='Previous month'
            onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
            disabled={loading}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant='h6'
            sx={{ minWidth: 180, textAlign: 'center', color: 'text.primary' }}
          >
            {periodLabel(period)}
          </Typography>
          <IconButton
            aria-label='Next month'
            onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
            disabled={loading || atCurrentMonth}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </GridItem>

      {loading && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        </GridItem>
      )}

      {error && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Alert severity='error' sx={{ mt: 2, maxWidth: 360, mx: 'auto' }}>
            {error}
          </Alert>
        </GridItem>
      )}

      {!loading && !error && statement && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ maxWidth: 360, mx: 'auto', mt: 2, textAlign: 'left' }}>
            <List dense disablePadding>
              {/* Opening balance */}
              <ListItem
                disableGutters
                sx={{ py: 0.75, alignItems: 'center' }}
              >
                <Box sx={{ minWidth: 56, mr: 1.5, flexShrink: 0 }} />
                <Typography
                  variant='body2'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 'bold',
                    color: 'text.primary',
                  }}
                >
                  Opening balance
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 'bold',
                    ml: 1,
                    minWidth: 64,
                    textAlign: 'right',
                    flexShrink: 0,
                    color: 'text.primary',
                  }}
                >
                  {dollars(statement.opening_balance_cents)}
                </Typography>
              </ListItem>

              {/* Entries */}
              {statement.entries.length === 0 ? (
                <ListItem disableGutters sx={{ py: 1.5 }}>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    No activity in {periodLabel(period)}.
                  </Typography>
                </ListItem>
              ) : (
                statement.entries.map((entry) => {
                  const negative = entry.amount_cents < 0;
                  const key = `${entry.type}-${entry.id}`;
                  const { date, time } = formatDateParts(entry.created_at);
                  // Top-ups and refunds both deep-link to the Stripe
                  // receipt page. Refund URLs come from the offsetting
                  // top-up — populated by step 12's charge.refunded
                  // webhook (not yet built); until then refunds get a
                  // placeholder click that does nothing.
                  const linkLike =
                    entry.type === 'top_up' || entry.type === 'refund';
                  const onClick = linkLike
                    ? () => {
                        if (entry.stripe_receipt_url) {
                          window.open(
                            entry.stripe_receipt_url,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        }
                      }
                    : undefined;
                  return (
                    <React.Fragment key={key}>
                      <Divider component='li' />
                      <ListItem
                        disableGutters
                        sx={{
                          cursor: onClick ? 'pointer' : 'default',
                          py: 0.75,
                          alignItems: 'center',
                        }}
                        onClick={onClick}
                      >
                        <Box sx={{ minWidth: 56, mr: 1.5, flexShrink: 0 }}>
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                          >
                            {date}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                          >
                            {time}
                          </Typography>
                        </Box>
                        <Typography
                          variant='body2'
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: linkLike ? 'primary.main' : 'text.primary',
                            textDecoration: linkLike ? 'underline' : 'none',
                          }}
                        >
                          {entryLabel(entry)}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: negative ? 'error.main' : 'success.main',
                            fontWeight: 'bold',
                            ml: 1,
                            minWidth: 64,
                            textAlign: 'right',
                            flexShrink: 0,
                          }}
                        >
                          {dollars(entry.amount_cents)}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  );
                })
              )}

              {/* Closing balance */}
              <Divider component='li' />
              <ListItem
                disableGutters
                sx={{ py: 0.75, alignItems: 'center' }}
              >
                <Box sx={{ minWidth: 56, mr: 1.5, flexShrink: 0 }} />
                <Typography
                  variant='body2'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 'bold',
                    color: 'text.primary',
                  }}
                >
                  Closing balance
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 'bold',
                    ml: 1,
                    minWidth: 64,
                    textAlign: 'right',
                    flexShrink: 0,
                    color: 'text.primary',
                  }}
                >
                  {dollars(statement.closing_balance_cents)}
                </Typography>
              </ListItem>
            </List>
          </Box>
        </GridItem>
      )}
    </AuthenticatedContainer>
  );
}
